/**
 * A condition, as one line a person can read.
 *
 *   { all: [{ field: 'scheme', operator: 'equals', value: 'reverseCharge' },
 *           { field: 'locationClass', operator: 'equals', value: 'domestic' }] }
 *   → "scheme = reverseCharge and locationClass = domestic"
 *
 * For showing a stored predicate where nobody can edit it — a posting rule's
 * `match`, today — so the reader can see what it actually tests rather than
 * trusting a name that was written separately and may have drifted.
 *
 * Field names and values are printed as stored: they are identifiers from the
 * data (a tax code attribute, a return box), not prose, and translating them
 * would hide the thing the reader is checking. Comparisons are symbols for the
 * same reason; only the few operators with no symbol get a short word.
 *
 * Returns '' for a missing or empty condition, which by evaluateCondition's rule
 * holds for everything — callers decide how to say that.
 */

const SYMBOLS = {
    equals: '=',
    notEquals: '≠',
    gt: '>',
    gte: '≥',
    lt: '<',
    lte: '≤',
    in: 'in',
    notIn: 'not in',
    contains: 'includes',
    containsAny: 'includes any of',
    minLength: 'has at least',
}

const UNARY = {
    exists: 'is set',
    notEmpty: 'is set',
    notExists: 'is not set',
    empty: 'is empty',
}

const formatValue = (value) => {
    if (Array.isArray(value)) return `[${value.map(formatValue).join(', ')}]`
    if (value === null || value === undefined) return '—'
    return String(value)
}

const leaf = (c) => {
    const operator = c.operator ?? 'equals'
    if (UNARY[operator]) return `${c.field} ${UNARY[operator]}`
    const right = c.valueFrom !== undefined ? c.valueFrom : formatValue(c.value)
    return `${c.field} ${SYMBOLS[operator] ?? operator} ${right}`
}

/**
 * @param {object|Array} condition
 * @param {boolean} nested  wrap a multi-part group in parentheses, so
 *                          `a and (b or c)` does not read as `(a and b) or c`
 */
const describe = (condition, nested = false) => {
    if (!condition || typeof condition !== 'object') return ''

    const group = (parts, word) => {
        const text = parts.map(p => describe(p, true)).filter(Boolean)
        if (text.length === 0) return ''
        const joined = text.join(` ${word} `)
        return nested && text.length > 1 ? `(${joined})` : joined
    }

    // Quantifiers first — the rest of the object is the per-row condition, not
    // a sibling clause (same order evaluateCondition uses).
    if (typeof condition.some === 'string') {
        const { some, ...inner } = condition
        return `some ${some} where ${describe(inner, true)}`
    }
    if (typeof condition.none === 'string') {
        const { none, ...inner } = condition
        return `no ${none} where ${describe(inner, true)}`
    }
    if (Array.isArray(condition.all)) return group(condition.all, 'and')
    if (Array.isArray(condition.any)) return group(condition.any, 'or')
    if (Array.isArray(condition)) return group(condition, 'and')
    if (condition.field === undefined) return ''
    return leaf(condition)
}

export const describeCondition = (condition) => describe(condition, false)

export default describeCondition
